<template>
  <component
    :is="tag"
    ref="container"
    class="site-header__navigation-item"
    @keydown.esc="handleEscape"
  >
    <template v-if="linklist.items?.length">
      <button
        ref="toggle"
        class="site-header__navigation-link text-other-label-medium"
        :class="[
          {
            [$class('active')]: showMegaNav,
          },
        ]"
        type="button"
        @mouseenter="handleHover"
        @keydown.enter.prevent="showMegaNav = !showMegaNav"
      >
        <span
          class="visually-hidden"
          v-html="
            $string('general.site_header.show_hide_tier', {
              replace: {
                action: showMegaNav ? 'Hide' : 'Show',
                title: linklist.title,
              },
            })
          "
        />
        <span
          aria-hidden="true"
          v-html="linklist.title"
        />
      </button>

      <transition name="fade">
        <mega-nav
          v-show="showMegaNav"
          :items="linklist.items"
          :image="linklist.image ? linklist.image : {}"
          :parent-hover-refresh="megaNavKey"
          :column-counts="getMaxColumns(linklist.columnCounts)"
        />
      </transition>
    </template>

    <a
      v-else
      class="site-header__navigation-link site-header__navigation-link--link text-other-label-medium"
      :href="linklist.url"
      @mouseenter="closeMegaNavs"
      @click="trackNavigationClick('nav_menu_click', $event, { jh_source: 'Header' })"
    >
      <span v-html="linklist.title" />
    </a>
  </component>
</template>

<script>
/**
 * Vue: Site header link (site-header-link)
 * -----------------------------------------------------------------------------
 * A desktop site header link with optional mega nav
 *
 * @param {Number} columnCounts.tier2 - Max number of tier 2 items in a column
 * @param {Number} columnCounts.tier3 - Max number of tier 3 items in a column
 * @param {Object} linklist - Tier 1 menu item
 * @param {String} [tag] - Name of HTML element tag
 *
 */

import { mapActions } from 'vuex';
import { trackNavigationClick } from '~/helpers/datalayer-actions';

import { generateUUID } from '~/helpers/general';
import MegaNav from '~global/mega-nav/mega-nav';

export default {
  name: 'SiteHeaderLink',

  components: {
    MegaNav,
  },

  props: {
    columnCounts: {
      type: Object,
      default: () => ({
        tier2: 5,
        tier3: 6,
      }),
    },
    linklist: {
      type: Object,
      default: () => ({
        title: '',
        items: [],
        url: '',
      }),
    },
    tag: {
      type: String,
      default: 'li',
    },
  },

  data() {
    return {
      uid: generateUUID(),
      showMegaNav: false,
      megaNavKey: new Date().getTime(),
    }
  },

  watch: {
    /**
     * When a mega nav is visible,
     * - Close all other mega navs
     * - Close search drawer
     * - Set focus traps
     */
    showMegaNav(isVisible) {
      if (isVisible) {
        this.closeMegaNavs(this.uid)
        this.closeOverlay('searchDrawer')
        this.setFocusTrap({
          container: this.$refs.container,
        })
      } else {
        this.clearFocusTrap()
      }
    },
  },

  mounted() {
    cnvs.EventBus.listen('mega-nav:visible', (uid) => {
      if (uid === this.uid || !this.showMegaNav) {
        return
      }
      this.showMegaNav = false
    })
  },

  methods: {
    trackNavigationClick,

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      clearFocusTrap: 'accessibility/clearFocusTrap',
      setFocusTrap: 'accessibility/setFocusTrap',
    }),

    /**
     * Close all mega navs when toggle is focused
     * @param {String} [excludedId] - UID of a mega nav to exclude from being
     * closed.
     */
    closeMegaNavs(excludedId) {
      cnvs.EventBus.emit('mega-nav:visible', excludedId)
    },

    /**
     * Get column counts by merging global settings and override settings.
     * Will prefer non-zero values
     * @param overrideColumns - Column settings from mega nav block
     * @returns {{tier3: (number), tier2: (number)}}
     */
    getMaxColumns(overrideColumns) {
      return {
        ...(this.columnCounts || {}),
        tier2: overrideColumns?.tier2 || this.columnCounts?.tier2 || 5,
        tier3: overrideColumns?.tier3 || this.columnCounts?.tier3 || 6,
      }
    },

    /**
     * Close mega nav on escape key and focus toggle button
     */
    handleEscape() {
      this.showMegaNav = false
      if (this.$refs.toggle) {
        this.$refs.toggle.focus()
      }
    },

    /**
     * Show mega nav on hover and reset mega nav's current image
     */
    handleHover() {
      if (this.$refs.toggle) {
        this.$refs.toggle.focus()
      }
      this.showMegaNav = true
      this.megaNavKey = new Date().getTime()
    },
  },
}
</script>
